import { Button, Tooltip } from '@loomhq/lens';
import pluralize from 'pluralize';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectVideoViews } from 'selectors/videoPlayer';

import ViewerInsightsModal from './modal';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import { ViewerAvatar } from './components';

const MAX_AVATARS = 3;

export default ({ user }) => {
  // no insights unless current viewer is logged in
  if (!user?.id) {
    return null;
  }

  const modalContainer = useRef(null);

  const views = useSelector(selectVideoViews);
  const [modalShown, setModalShown] = useState(false);

  const totalViews = views?.total || 0;
  const distinctViews = views?.distinct || 0;
  const namedViews = views?.named || [];
  const viewers = getFirstViewerAvatars(namedViews, distinctViews);
  const closeModal = () => setModalShown(false);

  useOnClickOutside(modalContainer, closeModal);

  return (
    <div className="relative mr:small self:center" ref={modalContainer}>
      <Tooltip content="Engagement insights" placement="bottomLeft">
        <Button onClick={() => setModalShown(!modalShown)}>
          <div className="flex items:center">
            {viewers.length ? <Avatars viewers={viewers} /> : null}
            {totalViews} {pluralize('view', Number(totalViews))}
          </div>
        </Button>
        {modalShown && <ViewerInsightsModal closeModal={closeModal} />}
      </Tooltip>
    </div>
  );
};

const Avatars = ({ viewers = [] }) => (
  <div className="flex mr:small ml:xsmall">
    {viewers.map((viewer = {}, i) => (
      <div key={i} className="viewer-insights-button-avatar-container">
        <ViewerAvatar size={3} {...viewer} />
      </div>
    ))}
  </div>
);

// given the named viewers and the total number of distinct views, this function
// prepares the viewers so they can be shown as avatars. it limits the number to
// MAX_AVATARS, and adds avatars for anonymous viewers, if there is room within
// MAX_AVATARS for them. the function returns the array of viewers that will be
// rendered as avatars
function getFirstViewerAvatars(namedViewers = [], total = 0) {
  const remaining = Math.max(0, total - MAX_AVATARS);
  const namedCount = namedViewers.length;

  if (namedCount >= MAX_AVATARS) {
    return namedViewers.slice(0, MAX_AVATARS);
  }

  const anonymousCount = total - namedCount - remaining;

  if (anonymousCount <= 0) {
    return namedViewers;
  }

  const result = [...namedViewers];

  for (let i = 0; i < anonymousCount; i++) {
    result.push({});
  }

  return result;
}
